import { useCallback, useEffect, useRef, useState } from "react";
import {
  Building2,
  Check,
  Copy,
  Globe,
  Hash,
  Settings2,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { isApiError } from "../../api/client.js";
import { getOrganization, updateOrganization } from "../../api/organizations-api.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import { Button } from "../../components/ui/button/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { SimpleTooltip } from "../../components/ui/tooltip/index.js";
import { useAuth } from "../auth/auth-context.js";
import { useNotifications } from "../notifications/notification-context.js";
import {
  ORGANIZATION_NAME_MAX_LENGTH,
  buildOrganizationPatch,
  canEditOrganizationSettings,
  canSubmitGeneralSettings,
  isGeneralFormDirty,
  isOrganizationNameValid,
  isTimezoneAllowed,
  snapshotFromOrganization,
  type GeneralSettingsSnapshot,
} from "./general-settings-form.js";
import { knownIanaTimezoneSet } from "./iana-timezones.js";
import { TimezoneSelect } from "./TimezoneSelect.js";
import { SettingsSectionPage } from "./SettingsSectionPage.js";
import { useSettingsWorkspace } from "./SettingsWorkspaceLayout.js";

const FIELD_CLASS =
  "h-10 w-full min-w-0 rounded-xl border border-line-strong bg-surface px-3.5 text-[0.8125rem] font-medium text-foreground transition-[border-color,box-shadow,background-color] duration-150 ease-in-out focus:border-[#163a37] focus:shadow-[0_0_0_3px_rgb(52_211_153/0.2)] focus:outline-none disabled:cursor-not-allowed disabled:bg-surface-muted/50 disabled:opacity-70";

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function orgMonogram(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "FB";
  const words = trimmed.split(/\s+/).filter(Boolean);
  const first = words[0];
  const second = words[1];
  if (first && second) {
    return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

export function GeneralSettingsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { organizationId, role } = useSettingsWorkspace();
  const { applyOrganizationName } = useAuth();
  const { pushNotification } = useNotifications();
  const canEdit = canEditOrganizationSettings(role);
  const knownTimezones = knownIanaTimezoneSet();
  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;

  const [loaded, setLoaded] = useState<GeneralSettingsSnapshot | null>(null);
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [loadNonce, setLoadNonce] = useState(0);

  const applyDetails = useCallback((details: { id: number; name: string; timezone: string }) => {
    const snapshot = snapshotFromOrganization(details);
    setLoaded(snapshot);
    setName(snapshot.name);
    setTimezone(snapshot.timezone);
    setNameTouched(false);
    setSaveError(null);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    setLoaded(null);
    setName("");
    setTimezone("");
    setSaving(false);
    setSaveError(null);
    setNameTouched(false);
    setCopied(false);

    void getOrganization(organizationId, controller.signal)
      .then((details) => {
        if (controller.signal.aborted) return;
        applyDetails(details);
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setLoaded(null);
        setLoading(false);
        setLoadError(isApiError(error) ? error.message : t("settings.general.loadError"));
      });

    return () => controller.abort();
  }, [organizationId, loadNonce, applyDetails, t]);

  const dirty = isGeneralFormDirty(loaded, name, timezone);
  const nameValid = isOrganizationNameValid(name);
  const timezoneValid = loaded
    ? isTimezoneAllowed(timezone, loaded.timezone, knownTimezones)
    : false;
  const canSubmit = canSubmitGeneralSettings({
    role,
    loadedOrganizationId: loaded?.organizationId ?? null,
    selectedOrganizationId: organizationId,
    dirty,
    saving,
    nameValid,
    timezoneValid,
  });
  const showNameError = canEdit && nameTouched && !nameValid;

  async function handleSave(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setNameTouched(true);
    if (!canSubmit || !loaded || saving) return;
    const patch = buildOrganizationPatch(loaded, name, timezone);
    if (!patch) return;

    const targetId = loaded.organizationId;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await updateOrganization(targetId, patch);
      applyOrganizationName(result.id, result.name);
      if (organizationIdRef.current !== result.id) {
        return;
      }
      applyDetails(result);
      pushNotification({ message: t("settings.general.saved") });
    } catch (error) {
      if (organizationIdRef.current !== targetId) return;
      setSaveError(
        isApiError(error) ? error.message : t("settings.general.saveError"),
      );
    } finally {
      if (organizationIdRef.current === targetId) {
        setSaving(false);
      }
    }
  }

  async function handleCopyId(): Promise<void> {
    if (!loaded) return;
    const ok = await copyText(String(loaded.organizationId));
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  }

  return (
    <SettingsSectionPage
      testId="settings-general-page"
      icon={Settings2}
      title={t("settings.general.title")}
      description={t("settings.general.description")}
      actions={
        canEdit ? (
          <Button
            type="submit"
            form="settings-general-form"
            size="sm"
            disabled={!canSubmit}
            className="rounded-xl px-4 font-semibold shadow-xs"
            data-testid="settings-general-save"
          >
            {saving ? t("settings.general.saving") : t("settings.general.save")}
          </Button>
        ) : null
      }
    >
      <div
        className="mx-auto grid w-full max-w-[68rem] gap-5"
        data-testid="settings-general-actions"
      >
        {loading ? (
          <GeneralSettingsSkeleton />
        ) : loadError ? (
          <ProductErrorState
            testId="settings-general-error"
            title={t("settings.general.loadErrorTitle")}
            description={t("settings.general.loadErrorBody")}
            onRetry={() => setLoadNonce((value) => value + 1)}
            retryLabel={t("settings.general.retry")}
            retryTestId="settings-general-retry"
          />
        ) : loaded ? (
          <form
            id="settings-general-form"
            className="overflow-hidden rounded-2xl border border-line/70 bg-surface shadow-[0_8px_30px_-18px_rgb(19_42_39/0.12)]"
            onSubmit={(event) => void handleSave(event)}
            noValidate
            data-testid="settings-general-form"
          >
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line/60 bg-[linear-gradient(135deg,#132a27_0%,#1a3a36_100%)] px-6 py-5 text-white">
              <div className="flex min-w-0 items-center gap-3.5">
                <span
                  className="inline-grid size-12 shrink-0 place-items-center rounded-2xl bg-[#34d399]/20 text-[0.95rem] font-bold tracking-wide text-[#34d399] ring-1 ring-[#34d399]/35"
                  aria-hidden="true"
                >
                  {orgMonogram(name || loaded.name)}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="m-0 truncate text-[1.0625rem] font-bold tracking-[-0.01em] text-white">
                      {name.trim() || loaded.name}
                    </p>
                    <span className="inline-flex items-center rounded-full bg-[#34d399]/15 px-2.5 py-0.5 text-[0.6875rem] font-semibold text-[#34d399] ring-1 ring-[#34d399]/30">
                      {t(`organization.role.${role.toLowerCase()}`)}
                    </span>
                  </div>
                  <p className="m-0 mt-0.5 text-[0.75rem] text-white/70">
                    {t("settings.general.description")}
                  </p>
                </div>
              </div>

              {dirty ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-3 py-1 text-[0.75rem] font-medium text-amber-200 ring-1 ring-amber-400/30">
                  <span className="size-1.5 rounded-full bg-amber-300" />
                  {t("settings.general.save")}
                </span>
              ) : null}
            </div>

            <SettingsRow
              icon={Building2}
              label={t("settings.general.nameLabel")}
              hint={t("settings.general.nameHint")}
              htmlFor="settings-general-name"
            >
              <div className="relative">
                <input
                  id="settings-general-name"
                  data-testid="settings-general-name"
                  type="text"
                  className={FIELD_CLASS}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  onBlur={() => setNameTouched(true)}
                  maxLength={ORGANIZATION_NAME_MAX_LENGTH}
                  autoComplete="organization"
                  disabled={!canEdit || saving}
                  readOnly={!canEdit}
                  aria-invalid={showNameError}
                  aria-describedby={showNameError ? "settings-general-name-error" : undefined}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <p
                  id="settings-general-name-error"
                  className="m-0 min-h-[1.1rem] text-[0.75rem] font-medium text-error"
                  role={showNameError ? "alert" : undefined}
                >
                  {showNameError ? t("settings.general.nameRequired") : "\u00a0"}
                </p>
                <span className="text-[0.6875rem] tabular-nums text-muted">
                  {name.length}/{ORGANIZATION_NAME_MAX_LENGTH}
                </span>
              </div>
            </SettingsRow>

            <div className="h-px bg-line/60" />

            <SettingsRow
              icon={Globe}
              label={t("settings.general.timezoneLabel")}
              hint={t("settings.general.timezoneHint")}
              htmlFor="settings-general-timezone"
            >
              <TimezoneSelect
                id="settings-general-timezone"
                value={timezone}
                onChange={setTimezone}
                disabled={!canEdit || saving}
              />
            </SettingsRow>

            <div className="h-px bg-line/60" />

            <SettingsRow
              icon={Hash}
              label={t("settings.general.organizationIdLabel")}
              hint={t("settings.general.organizationIdHint")}
            >
              <div className="flex min-w-0 items-center justify-between gap-3 rounded-xl border border-line/70 bg-canvas/60 px-3.5 py-2">
                <LtrValue className="min-w-0 truncate font-mono text-[0.8125rem] font-semibold tabular-nums text-foreground">
                  <span data-testid="settings-general-organization-id">
                    {String(loaded.organizationId)}
                  </span>
                </LtrValue>
                <SimpleTooltip
                  content={copied ? t("settings.general.copied") : t("settings.general.copyId")}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-7 gap-1.5 rounded-lg px-2.5 text-[0.75rem] font-medium text-muted hover:bg-surface hover:text-foreground"
                    aria-label={t("settings.general.copyId")}
                    data-testid="settings-general-copy-id"
                    onClick={() => void handleCopyId()}
                  >
                    {copied ? (
                      <>
                        <Check size={13} className="text-[#10b981]" aria-hidden="true" />
                        <span className="text-[#10b981]">{t("settings.general.copied")}</span>
                      </>
                    ) : (
                      <>
                        <Copy size={13} aria-hidden="true" />
                        <span>{t("settings.general.copyId")}</span>
                      </>
                    )}
                  </Button>
                </SimpleTooltip>
              </div>
            </SettingsRow>

            {saveError ? (
              <div className="border-t border-error/20 bg-error-muted/40 px-6 py-3.5">
                <p
                  className="m-0 text-[0.8125rem] font-medium text-error"
                  role="alert"
                  data-testid="settings-general-save-error"
                >
                  {saveError}
                </p>
              </div>
            ) : null}
          </form>
        ) : null}
      </div>
    </SettingsSectionPage>
  );
}

function SettingsRow({
  icon: Icon,
  label,
  hint,
  htmlFor,
  children,
}: {
  icon: LucideIcon;
  label: string;
  hint: string;
  htmlFor?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="grid gap-3.5 px-6 py-5 sm:grid-cols-[minmax(14rem,18rem)_minmax(0,1fr)] sm:items-start sm:gap-8">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-grid size-8 shrink-0 place-items-center rounded-xl border border-[#163a37]/12 bg-[#163a37]/[0.05] text-[#163a37]">
          <Icon size={15} aria-hidden="true" />
        </span>
        <div className="grid gap-1">
          {htmlFor ? (
            <label
              className="text-[0.84375rem] font-semibold text-foreground"
              htmlFor={htmlFor}
            >
              {label}
            </label>
          ) : (
            <p className="m-0 text-[0.84375rem] font-semibold text-foreground">{label}</p>
          )}
          <p className="m-0 text-[0.75rem] leading-relaxed text-muted">{hint}</p>
        </div>
      </div>
      <div className="grid min-w-0 max-w-[32rem] gap-1">{children}</div>
    </div>
  );
}

function GeneralSettingsSkeleton(): React.JSX.Element {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-line/70 bg-surface"
      data-testid="settings-general-loading"
      aria-busy="true"
    >
      <div className="h-[5.5rem] animate-pulse bg-[#132a27]/90" />
      {[0, 1, 2].map((index) => (
        <div key={index}>
          {index > 0 ? <div className="h-px bg-line/60" /> : null}
          <div className="grid gap-3.5 px-6 py-5 sm:grid-cols-[minmax(14rem,18rem)_minmax(0,1fr)] sm:gap-8">
            <div className="flex items-start gap-3">
              <div className="size-8 animate-pulse rounded-xl bg-surface-muted" />
              <div className="grid flex-1 gap-2">
                <div className="h-3.5 w-28 animate-pulse rounded bg-surface-muted" />
                <div className="h-3 w-40 animate-pulse rounded bg-surface-muted" />
              </div>
            </div>
            <div className="h-10 max-w-[32rem] animate-pulse rounded-xl bg-surface-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}
